import { useCallback } from "react";
import { JSONSchema7 } from "json-schema";
import { toast } from "sonner";

/**
 * Merges query parameters schema, body parameters schema, and path parameters schema with conflict detection
 * @param queryParamsSchema - Schema for query parameters
 * @param bodyParamsSchema - Schema for body parameters
 * @param pathParamsSchema - Schema for path parameters
 * @returns Merged JSONSchema7 object
 */
export function mergeSchemas(
  queryParamsSchema: JSONSchema7 | undefined,
  bodyParamsSchema: JSONSchema7 | undefined,
  pathParamsSchema: JSONSchema7 | undefined
): JSONSchema7 {
  const queryProperties = queryParamsSchema?.properties || {};
  const bodyProperties = bodyParamsSchema?.properties || {};
  const pathProperties = pathParamsSchema?.properties || {};
  const queryRequired = queryParamsSchema?.required || [];
  const bodyRequired = bodyParamsSchema?.required || [];
  const pathRequired = pathParamsSchema?.required || [];

  // Track different types of conflicts
  const bodyOverridesPath: string[] = [];
  const bodyOverridesQuery: string[] = [];
  const pathOverridesQuery: string[] = [];
  const mergedProperties: Record<string, JSONSchema7> = {};

  // Track which parameters exist in which locations
  const parameterLocations: Record<string, { query?: boolean; path?: boolean; body?: boolean }> = {};
  
  // Catalog all parameter locations
  Object.keys(queryProperties).forEach(key => {
    if (!parameterLocations[key]) parameterLocations[key] = {};
    parameterLocations[key].query = true;
  });
  
  Object.keys(pathProperties).forEach(key => {
    if (!parameterLocations[key]) parameterLocations[key] = {};
    parameterLocations[key].path = true;
  });
  
  Object.keys(bodyProperties).forEach(key => {
    if (!parameterLocations[key]) parameterLocations[key] = {};
    parameterLocations[key].body = true;
  });

  // Add query parameters first
  Object.entries(queryProperties).forEach(([key, schema]) => {
    if (typeof schema === "object" && schema !== null) {
      mergedProperties[key] = schema as JSONSchema7;
    }
  });

  // Add path parameters and check for conflicts
  Object.entries(pathProperties).forEach(([key, schema]) => {
    if (typeof schema === "object" && schema !== null) {
      const pathSchema = schema as JSONSchema7;
      if (mergedProperties[key]) {
        pathOverridesQuery.push(key);
        // Path parameters take precedence over query parameters
        mergedProperties[key] = {
          ...pathSchema,
          description: `${pathSchema.description || key} (Path parameter - overrides query parameter)`.trim(),
        };
      } else {
        mergedProperties[key] = pathSchema;
      }
    }
  });

  // Add body parameters and check for conflicts
  Object.entries(bodyProperties).forEach(([key, schema]) => {
    if (typeof schema === "object" && schema !== null) {
      const bodySchema = schema as JSONSchema7;
      const locations = parameterLocations[key];
      
      if (mergedProperties[key]) {
        if (locations?.path) {
          bodyOverridesPath.push(key);
        } else if (locations?.query) {
          bodyOverridesQuery.push(key);
        }
        
        // Body parameters take precedence in case of conflict
        const conflictNote = locations?.path ? 
          "(Body parameter - overrides path parameter)" : 
          "(Body parameter - overrides query parameter)";
        mergedProperties[key] = {
          ...bodySchema,
          description: `${bodySchema.description || key} ${conflictNote}`.trim(),
        };
      } else {
        mergedProperties[key] = bodySchema;
      }
    }
  });

  // Only show warning for significant conflicts (body overriding path/query)
  // We'll use console.warn instead of toast to be less intrusive
  const significantConflicts = [...bodyOverridesPath, ...bodyOverridesQuery];
  if (significantConflicts.length > 0) {
    console.warn(
      `Parameter name conflicts detected: ${significantConflicts.join(
        ", "
      )}. Body parameters will take precedence over path/query parameters.`
    );
  }
  
  // Log path-query conflicts as info only (less severe)
  if (pathOverridesQuery.length > 0) {
    console.info(
      `Path parameters override query parameters: ${pathOverridesQuery.join(", ")}`
    );
  }

  // Merge required arrays
  const allRequired = [
    ...new Set([...queryRequired, ...pathRequired, ...bodyRequired]),
  ];

  return {
    type: "object",
    properties: mergedProperties,
    required: allRequired,
    title: "API Parameters",
    description:
      "Combined query parameters, path parameters, and request body schema",
    // Add subschemas for reference
    ...(queryParamsSchema && {
      definitions: {
        queryParameters: queryParamsSchema,
        ...(pathParamsSchema && { pathParameters: pathParamsSchema }),
        ...(bodyParamsSchema && { requestBody: bodyParamsSchema }),
      },
    }),
  };
}

/**
 * Custom hook for merging schemas with conflict detection
 * @returns Object containing the merge function and related utilities
 */
export function useMergeSchemas() {
  return useCallback(
    (
      queryParamsSchema: JSONSchema7 | undefined,
      bodyParamsSchema: JSONSchema7 | undefined,
      pathParamsSchema: JSONSchema7 | undefined
    ) => {
      return mergeSchemas(
        queryParamsSchema,
        bodyParamsSchema,
        pathParamsSchema
      );
    },
    []
  );
}
